import type { ProShiftAvailabilitySlice, TimeBlock } from "@sidegigllc/api/client";

/** Absorbs the minute an inclusive block end (`11:59`) leaves behind. */
const INCLUSIVE_END_TOLERANCE = 1;

const toMinutes = (time: string) => {
  const [hours = 0, minutes = 0] = time.split(":").map(Number);
  return hours * 60 + minutes;
};

/** The API sends midnight as either `"24:00:00"` or `"00:00:00"`. */
const endMinutes = (time: string) => toMinutes(time) || 24 * 60;

const formatMinutes = (minute: number) => {
  const hours24 = Math.floor(minute / 60) % 24;
  const minutes = minute % 60;
  const suffix = hours24 < 12 ? "am" : "pm";
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  return `${hours12}:${String(minutes).padStart(2, "0")} ${suffix}`;
};

/**
 * The backend merges touching intervals, so all three blocks store as one `05:00-24:00` span that
 * matches none of them and returns `time_block_name: null`. Splitting it back mirrors the pro app's
 * `decomposeSlice`; anything no block covers stays as hours so a custom range is never hidden.
 */
export function describeSlice(slice: ProShiftAvailabilitySlice, blocks: ReadonlyArray<TimeBlock>): string {
  if (slice.time_block_name) return slice.time_block_name;

  const start = toMinutes(slice.start_time);
  const end = endMinutes(slice.end_time);
  const covered = blocks
    .map((block) => ({ name: block.name, start: toMinutes(block.start_time), end: endMinutes(block.end_time) }))
    .filter((block) => block.start < block.end && block.start >= start && block.end <= end)
    .toSorted((a, b) => a.start - b.start);

  if (covered.length === 0) return `${formatMinutes(start)} – ${formatMinutes(end)}`;

  const parts: Array<string> = [];
  let cursor = start;
  const pushRemainder = (from: number, to: number) => {
    if (to - from > INCLUSIVE_END_TOLERANCE) parts.push(`${formatMinutes(from)} – ${formatMinutes(to)}`);
  };

  for (const block of covered) {
    // Overlaps an already-named block; naming it again would double-count the minutes.
    if (block.start < cursor) continue;
    pushRemainder(cursor, block.start);
    parts.push(block.name);
    cursor = block.end;
  }
  pushRemainder(cursor, end);
  return parts.join(", ");
}
